import { useEffect, useMemo, useState } from 'react';

import { MAIN_BRANCH_ID } from '@polyai/stream/constants';
import { agentVoiceSelectors } from '@polyai/stream/store/reducers/voices';
import Button from '@polyai/ui/components/atoms/Button';
import { TimeIcon } from '@polyai/ui/components/atoms/Icons';
import LoadingIcon from '@polyai/ui/components/atoms/LoadingIcon';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';

import Helmet from 'components/molecules/Helmet';
import useProjection from 'hooks/agentStream/projection/useProjection';
import { useVoiceOptions } from 'hooks/api/config/useVoiceOptions';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import useProjectRoute from 'hooks/useProjectRoute';
import VoiceOptionButton from 'screens/AgentVoicesStream/components/VoiceOptionButton';
import { ContentContainer } from 'screens/AgentWizard/AgentWizard.styled';
import { LeftPanel } from 'screens/AgentWizard/components/LeftPanel';
import ProgressSliderWrapper from 'screens/AgentWizard/components/ProgressSliderWrapper';
import { Wrapper } from 'screens/AgentWizard/components/Wrapper';
import {
  ASSETS,
  WIZARD_CONTENT_WIDTH,
  WIZARD_STEPS,
} from 'screens/AgentWizard/constants';
import { LoadingButton } from './components/LoadingButton';
import SetupSteps from './components/SetupSteps';
import { useAgentSetupFlow } from './hooks/useAgentSetup';
import useConfigUpdate from './hooks/useConfigUpdate';
import {
  Content,
  FixedBottomSection,
  FooterSticky,
  Logo,
  MobileLogo,
  RightPanel,
  ScrollableSection,
  Title,
} from './Voice.styled';

const DEFAULT_VOICE_OPTIONS_LIMIT = 5;

export interface AgentWizardVoiceSelectorProps {
  currentVoice?: string;
  loadingVoice?: string;
  showTitle?: boolean;
  onSelectVoice: (voiceId: string) => void;
}

export const AgentWizardVoiceSelector = ({
  currentVoice,
  loadingVoice,
  showTitle = true,
  onSelectVoice,
}: AgentWizardVoiceSelectorProps) => {
  const useCuratedVoices = useIsPlgProd();
  const voices = useVoiceOptions();

  const topVoices = useMemo(() => {
    const voiceList = useCuratedVoices
      ? voices.data?.voices?.filter(v => v.voice_metadata?.plg_curated)
      : voices.data?.voices?.slice(0, DEFAULT_VOICE_OPTIONS_LIMIT);

    // Sort voices to put US voices first (create a new array to avoid mutation)
    return voiceList
      ? [...voiceList].sort((a, b) => {
          const aIsUs = a.voice_metadata?.accent === 'en-US';
          const bIsUs = b.voice_metadata?.accent === 'en-US';
          return Number(bIsUs) - Number(aIsUs);
        })
      : voiceList;
  }, [voices.data, useCuratedVoices]);

  return (
    <>
      {showTitle && <Title>Select your agent&apos;s voice</Title>}
      <Stack
        data-test-id="agent-wizard-voice-options"
        direction="column"
        spacing="xs2"
      >
        {topVoices?.map(v => (
          <VoiceOptionButton
            key={v.id}
            isLoading={loadingVoice === v.id}
            isSelected={v.id === currentVoice}
            loadingVoiceId={loadingVoice}
            style={{ minWidth: `${WIZARD_CONTENT_WIDTH}px` }}
            voice={v}
            showFlag
            onClick={() => onSelectVoice(v.id)}
            onPlay={() => onSelectVoice(v.id)}
          />
        ))}
      </Stack>
    </>
  );
};

const AgentWizardVoice = () => {
  const { accountId, projectId } = useProjectRoute();

  const { mutate: updateConfig } = useConfigUpdate(projectId);

  const {
    navigateToScenarios,
    retryCount,
    isLoading: isAgentSetupLoading,
    retry: retryAgentSetup,
    progress,
    hasUnacceptableErrors,
  } = useAgentSetupFlow();

  const {
    data,
    refetch,
    error: projectionError,
  } = useProjection(accountId, projectId, MAIN_BRANCH_ID);

  const currentVoices = useMemo(
    () =>
      data?.projection
        ? agentVoiceSelectors.selectAll(data.projection.voices.agentVoices)
        : [],
    [data?.projection],
  );
  const [loadingVoiceId, setLoadingVoiceId] = useState<string | undefined>();
  const currentVoice = currentVoices[0];
  const currentVoiceId = currentVoice?.voiceId;

  useEffect(() => {
    if (projectionError || data?.lastKnownSequence !== undefined) {
      setLoadingVoiceId(undefined);
    }
  }, [data?.lastKnownSequence, projectionError]);

  const onClickVoice = (voiceId: string) => {
    // Don't allow clicking the same voice or if no current voice exists
    if (!currentVoiceId || voiceId === currentVoiceId || !currentVoice?.id) {
      return;
    }

    setLoadingVoiceId(voiceId);
    updateConfig(
      {
        body: {
          voiceId,
          voiceEntityId: currentVoice.id,
        },
      },
      {
        onSuccess: () => {
          void refetch();
        },
        onError: () => {
          setLoadingVoiceId(undefined);
        },
      },
    );
  };

  const handleContinue = async () => {
    return navigateToScenarios();
  };

  // Can continue if not loading and doesn't have unacceptable errors
  const canContinue = !isAgentSetupLoading && !hasUnacceptableErrors;

  return (
    <>
      <Helmet title="Agent Wizard - Voice" />
      <Wrapper>
        <LeftPanel bgUrl={ASSETS.LOGIN_BG}>
          <Logo aria-label="PolyAI" />
          <SetupSteps
            completedSteps={
              isAgentSetupLoading || hasUnacceptableErrors
                ? progress?.completedSteps
                : 5
            }
            failed={hasUnacceptableErrors}
          />
        </LeftPanel>
        <RightPanel>
          <MobileLogo aria-label="PolyAI" />
          <ProgressSliderWrapper step={WIZARD_STEPS.VOICE} showResetButton />
          <ContentContainer>
            <Content>
              <ScrollableSection>
                <AgentWizardVoiceSelector
                  currentVoice={currentVoiceId}
                  loadingVoice={loadingVoiceId}
                  onSelectVoice={onClickVoice}
                />
              </ScrollableSection>

              <FixedBottomSection>
                <FooterSticky>
                  {isAgentSetupLoading ? (
                    <LoadingButton
                      data-test-id="agent-wizard-voice-loading-button"
                      style={{ minWidth: `${WIZARD_CONTENT_WIDTH}px` }}
                    >
                      <LoadingIcon size={16} />
                      Building your agent
                    </LoadingButton>
                  ) : (
                    <Tooltip
                      content={
                        <Stack align="center" direction="row" spacing="xs1">
                          <TimeIcon color="iconSecondaryInverse" size="sm" />
                          <Text variant="bodySmall">
                            {hasUnacceptableErrors
                              ? `Analysis failed${
                                  retryCount > 0
                                    ? ` (attempt ${retryCount})`
                                    : ''
                                }. Click to retry.`
                              : 'Building an agent can take 5-10 minutes'}
                          </Text>
                        </Stack>
                      }
                      disabled={!hasUnacceptableErrors}
                      style={{ borderRadius: '12px' }}
                      complex
                    >
                      <Button
                        data-test-id="agent-wizard-voice-continue-button"
                        disabled={false}
                        label={
                          hasUnacceptableErrors ? 'Retry Analysis' : 'Continue'
                        }
                        style={{
                          width: '100%',
                          minWidth: `${WIZARD_CONTENT_WIDTH}px`,
                        }}
                        variant={
                          hasUnacceptableErrors ? 'SECONDARY' : 'PRIMARY'
                        }
                        onClick={canContinue ? handleContinue : retryAgentSetup}
                      />
                    </Tooltip>
                  )}
                </FooterSticky>
              </FixedBottomSection>
            </Content>
          </ContentContainer>
        </RightPanel>
      </Wrapper>
    </>
  );
};

export default AgentWizardVoice;
